import { useState } from 'react';
import { Plus, Trash2, Loader2, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  useEstablishmentNotices,
  EstablishmentNoticeInput,
} from '@/hooks/useEstablishmentNotices';
import {
  EstablishmentNotice,
  NoticeScope,
  NOTICE_MAX_LENGTH,
  WEEKDAY_LABELS,
} from '@/lib/establishmentNoticeUtils';
import { EstablishmentNoticeAlert } from '@/components/EstablishmentNoticeAlert';
import { formatDateLocal, parseDateLocal } from '@/lib/timeFormatUtils';

interface EstablishmentNoticesSettingsProps {
  establishmentId?: string | null;
}

const emptyForm: EstablishmentNoticeInput = {
  message: '',
  scope: 'permanent',
  specificDate: null,
  weekday: null,
  requireAcknowledgement: false,
  isActive: true,
};

function describePeriod(notice: EstablishmentNotice): string {
  if (notice.scope === 'date' && notice.specificDate) {
    return parseDateLocal(notice.specificDate).toLocaleDateString('fr-CH', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }
  if (notice.scope === 'weekday' && notice.weekday !== null) {
    return `Tous les ${WEEKDAY_LABELS[notice.weekday].toLowerCase()}s`;
  }
  return 'Affichage permanent';
}

export function EstablishmentNoticesSettings({ establishmentId }: EstablishmentNoticesSettingsProps) {
  const { notices, isLoading, createNotice, updateNotice, deleteNotice, isSaving } =
    useEstablishmentNotices({ establishmentId });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EstablishmentNoticeInput>(emptyForm);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsFormOpen(true);
  };

  const openEdit = (notice: EstablishmentNotice) => {
    setEditingId(notice.id);
    setForm({
      message: notice.message,
      scope: notice.scope,
      specificDate: notice.specificDate,
      weekday: notice.weekday,
      requireAcknowledgement: notice.requireAcknowledgement,
      isActive: notice.isActive,
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const canSave =
    form.message.trim() !== '' &&
    (form.scope !== 'date' || !!form.specificDate) &&
    (form.scope !== 'weekday' || form.weekday !== null);

  const handleSave = async () => {
    if (!canSave) return;
    if (editingId) {
      await updateNotice({ id: editingId, input: form });
    } else {
      await createNotice(form);
    }
    closeForm();
  };

  const previewNotice: EstablishmentNotice = {
    id: 'preview',
    establishmentId: establishmentId ?? '',
    message: form.message,
    scope: form.scope,
    specificDate: form.specificDate ?? null,
    weekday: form.weekday ?? null,
    requireAcknowledgement: form.requireAcknowledgement,
    isActive: true,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">Notes optionnelles</Label>
          <p className="text-xs text-muted-foreground">
            Un message affiché au client après le choix de la date : en permanence, pour une date
            précise (soirée à thème, passeport gourmand…) ou tous les mêmes jours de la semaine.
            Cela n'empêche jamais de réserver.
          </p>
        </div>
        {!isFormOpen && (
          <Button type="button" variant="outline" size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Chargement des notes…
        </div>
      ) : notices.length === 0 && !isFormOpen ? (
        <p className="text-xs text-muted-foreground">
          Aucune note. La fonctionnalité reste invisible pour vos clients tant qu'aucune note
          active n'existe.
        </p>
      ) : (
        <div className="space-y-2">
          {notices.map((notice) => (
            <div
              key={notice.id}
              className="flex items-start justify-between gap-3 rounded-lg border p-3"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {describePeriod(notice)}
                  </Badge>
                  {notice.requireAcknowledgement && (
                    <Badge variant="secondary" className="text-xs">
                      Confirmation de lecture
                    </Badge>
                  )}
                  {!notice.isActive && (
                    <Badge variant="secondary" className="text-xs">
                      Désactivée
                    </Badge>
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap break-words">{notice.message}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Switch
                  checked={notice.isActive}
                  disabled={isSaving}
                  onCheckedChange={(checked) =>
                    updateNotice({
                      id: notice.id,
                      input: {
                        message: notice.message,
                        scope: notice.scope,
                        specificDate: notice.specificDate,
                        weekday: notice.weekday,
                        requireAcknowledgement: notice.requireAcknowledgement,
                        isActive: checked,
                      },
                    })
                  }
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(notice)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={isSaving}
                  onClick={() => deleteNotice(notice.id)}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isFormOpen && (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              {editingId ? 'Modifier la note' : 'Nouvelle note'}
            </Label>
            <Button type="button" variant="ghost" size="icon" onClick={closeForm}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            <Textarea
              value={form.message}
              onChange={(e) =>
                setForm((f) => ({ ...f, message: e.target.value.slice(0, NOTICE_MAX_LENGTH) }))
              }
              placeholder="Ex : Ce soir, menu unique à 65.— (soirée à thème). Le passeport gourmand n'est pas accepté ce jour-là."
              className="min-h-[90px]"
            />
            <div className="flex justify-end">
              <span
                className={`text-xs ${form.message.length > NOTICE_MAX_LENGTH - 50 ? 'text-amber-600' : 'text-muted-foreground'}`}
              >
                {form.message.length}/{NOTICE_MAX_LENGTH}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Période d'affichage</Label>
            <RadioGroup
              value={form.scope}
              onValueChange={(v) =>
                setForm((f) => ({
                  ...f,
                  scope: v as NoticeScope,
                  specificDate: v === 'date' ? f.specificDate : null,
                  weekday: v === 'weekday' ? f.weekday : null,
                }))
              }
              className="flex flex-col gap-2 sm:flex-row sm:gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="permanent" id="notice-scope-permanent" />
                <Label htmlFor="notice-scope-permanent" className="text-sm font-normal cursor-pointer">
                  Permanent
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="date" id="notice-scope-date" />
                <Label htmlFor="notice-scope-date" className="text-sm font-normal cursor-pointer">
                  Une date précise
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="weekday" id="notice-scope-weekday" />
                <Label htmlFor="notice-scope-weekday" className="text-sm font-normal cursor-pointer">
                  Chaque semaine
                </Label>
              </div>
            </RadioGroup>
          </div>

          {form.scope === 'date' && (
            <div className="space-y-2">
              <Label className="text-sm" htmlFor="notice-date">
                Date concernée
              </Label>
              <Input
                id="notice-date"
                type="date"
                value={form.specificDate ?? ''}
                min={formatDateLocal(new Date())}
                onChange={(e) => setForm((f) => ({ ...f, specificDate: e.target.value || null }))}
              />
            </div>
          )}

          {form.scope === 'weekday' && (
            <div className="space-y-2">
              <Label className="text-sm">Jour de la semaine</Label>
              <Select
                value={form.weekday !== null && form.weekday !== undefined ? String(form.weekday) : ''}
                onValueChange={(v) => setForm((f) => ({ ...f, weekday: Number(v) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un jour" />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAY_LABELS.map((label, index) => (
                    <SelectItem key={label} value={String(index)}>
                      Tous les {label.toLowerCase()}s
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="notice-ack" className="text-sm">
                Demander une confirmation de lecture
              </Label>
              <p className="text-xs text-muted-foreground">
                Le client doit cocher « J'ai lu cette note et je l'accepte » pour valider sa
                réservation.
              </p>
            </div>
            <Switch
              id="notice-ack"
              checked={form.requireAcknowledgement}
              onCheckedChange={(checked) =>
                setForm((f) => ({ ...f, requireAcknowledgement: checked }))
              }
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="notice-active" className="text-sm">
              Note active
            </Label>
            <Switch
              id="notice-active"
              checked={form.isActive}
              onCheckedChange={(checked) => setForm((f) => ({ ...f, isActive: checked }))}
            />
          </div>

          {form.message.trim() !== '' && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Aperçu client :</Label>
              <EstablishmentNoticeAlert notice={previewNotice} showAcknowledgement idPrefix="preview" />
            </div>
          )}

          <div className="flex gap-2">
            <Button type="button" onClick={handleSave} disabled={!canSave || isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement…
                </>
              ) : editingId ? (
                'Enregistrer les modifications'
              ) : (
                'Ajouter la note'
              )}
            </Button>
            <Button type="button" variant="outline" onClick={closeForm}>
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
